import React, { useState, useEffect } from 'react'
import { Link, Webhook, Mail, MessageSquare, Calendar, Shield, Check, X, Settings, Eye, EyeOff, TestTube } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface Integration {
  id: string
  name: string
  type: 'webhook' | 'email' | 'sms' | 'calendar' | 'api'
  status: 'connected' | 'disconnected' | 'error'
  description: string
  icon: React.ReactNode
  config: Record<string, any>
  lastSync?: string
  errorMessage?: string
}

export const IntegrationSettings: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [testingId, setTestingId] = useState<string | null>(null)
  const [showConfigModal, setShowConfigModal] = useState<string | null>(null)
  const [configForm, setConfigForm] = useState<Record<string, any>>({})
  const [showApiKeys, setShowApiKeys] = useState(false)

  const availableIntegrations: Integration[] = [
    {
      id: 'webhook',
      name: 'Webhooks',
      type: 'webhook',
      status: 'disconnected',
      description: 'Send real-time notifications to external services',
      icon: <Webhook className="w-5 h-5" />,
      config: {
        url: '',
        secret: '',
        events: []
      }
    },
    {
      id: 'email',
      name: 'Email Notifications',
      type: 'email',
      status: 'disconnected',
      description: 'Send email notifications for important events',
      icon: <Mail className="w-5 h-5" />,
      config: {
        smtp_host: '',
        smtp_port: 587,
        smtp_user: '',
        smtp_password: '',
        from_address: ''
      }
    },
    {
      id: 'sms',
      name: 'SMS Notifications',
      type: 'sms',
      status: 'disconnected',
      description: 'Send text messages for urgent notifications',
      icon: <MessageSquare className="w-5 h-5" />,
      config: {
        provider: 'twilio',
        account_sid: '',
        auth_token: '',
        from_number: ''
      }
    },
    {
      id: 'google_calendar',
      name: 'Google Calendar',
      type: 'calendar',
      status: 'disconnected',
      description: 'Sync chores and events with Google Calendar',
      icon: <Calendar className="w-5 h-5" />,
      config: {
        client_id: '',
        client_secret: '',
        refresh_token: ''
      }
    },
    {
      id: 'api',
      name: 'API Access',
      type: 'api',
      status: 'connected',
      description: 'Manage API keys and access tokens',
      icon: <Shield className="w-5 h-5" />,
      config: {
        api_keys: []
      }
    }
  ]

  useEffect(() => {
    loadIntegrations()
  }, [])

  const loadIntegrations = async () => {
    setLoading(true)
    try {
      const data = await apiClient.get('/api/integrations')
      const loadedIntegrations = availableIntegrations.map(integration => {
        const saved = data.find((i: any) => i.id === integration.id)
        if (saved) {
          return { ...integration, ...saved }
        }
        return integration
      })
      setIntegrations(loadedIntegrations)
    } catch (error) {
      console.error('Failed to load integrations:', error)
      setIntegrations(availableIntegrations)
    } finally {
      setLoading(false)
    }
  }

  const handleConfigure = (integration: Integration) => {
    setConfigForm(integration.config)
    setShowConfigModal(integration.id)
  }

  const handleSaveConfig = async () => {
    if (!showConfigModal) return

    try {
      await apiClient.put(`/api/integrations/${showConfigModal}`, {
        config: configForm,
        status: 'connected'
      })
      toast.success('Integration configured successfully')
      setShowConfigModal(null)
      loadIntegrations()
    } catch (error) {
      toast.error('Failed to save configuration')
    }
  }

  const handleTest = async (integrationId: string) => {
    setTestingId(integrationId)
    try {
      const result = await apiClient.post(`/api/integrations/${integrationId}/test`)
      if (result.success) {
        toast.success('Integration test successful')
      } else {
        toast.error(result.error || 'Integration test failed')
      }
    } catch (error) {
      toast.error('Failed to test integration')
    } finally {
      setTestingId(null)
    }
  }

  const handleDisconnect = async (integrationId: string) => {
    if (!confirm('Are you sure you want to disconnect this integration?')) return

    try {
      await apiClient.put(`/api/integrations/${integrationId}`, {
        status: 'disconnected',
        config: {}
      })
      toast.success('Integration disconnected')
      loadIntegrations()
    } catch (error) {
      toast.error('Failed to disconnect integration')
    }
  }

  const generateApiKey = async () => {
    try {
      const result = await apiClient.post('/api/integrations/api-keys')
      toast.success('API key generated successfully')
      loadIntegrations()
      return result.key
    } catch (error) {
      toast.error('Failed to generate API key')
    }
  }

  const revokeApiKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key?')) return

    try {
      await apiClient.delete(`/api/integrations/api-keys/${keyId}`)
      toast.success('API key revoked')
      loadIntegrations()
    } catch (error) {
      toast.error('Failed to revoke API key')
    }
  }

  const renderConfigModal = () => {
    if (!showConfigModal) return null

    const integration = integrations.find(i => i.id === showConfigModal)
    if (!integration) return null

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">Configure {integration.name}</h3>

          <div className="space-y-4">
            {integration.type === 'webhook' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Webhook URL</label>
                  <input
                    type="url"
                    value={configForm.url || ''}
                    onChange={(e) => setConfigForm({ ...configForm, url: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://example.com/webhook"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Secret Key (optional)</label>
                  <input
                    type="password"
                    value={configForm.secret || ''}
                    onChange={(e) => setConfigForm({ ...configForm, secret: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Your webhook secret"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Events to Send</label>
                  <div className="space-y-2">
                    {['chore.completed', 'chore.verified', 'points.earned', 'reward.claimed', 'user.login'].map(event => (
                      <label key={event} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={configForm.events?.includes(event)}
                          onChange={(e) => {
                            const events = configForm.events || []
                            if (e.target.checked) {
                              setConfigForm({ ...configForm, events: [...events, event] })
                            } else {
                              setConfigForm({ ...configForm, events: events.filter((e: string) => e !== event) })
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">{event}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {integration.type === 'email' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">SMTP Host</label>
                  <input
                    type="text"
                    value={configForm.smtp_host || ''}
                    onChange={(e) => setConfigForm({ ...configForm, smtp_host: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">SMTP Port</label>
                  <input
                    type="number"
                    value={configForm.smtp_port || 587}
                    onChange={(e) => setConfigForm({ ...configForm, smtp_port: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Username</label>
                  <input
                    type="text"
                    value={configForm.smtp_user || ''}
                    onChange={(e) => setConfigForm({ ...configForm, smtp_user: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="your-email@gmail.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Password</label>
                  <input
                    type="password"
                    value={configForm.smtp_password || ''}
                    onChange={(e) => setConfigForm({ ...configForm, smtp_password: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">From Address</label>
                  <input
                    type="email"
                    value={configForm.from_address || ''}
                    onChange={(e) => setConfigForm({ ...configForm, from_address: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="noreply@example.com"
                  />
                </div>
              </>
            )}

            {integration.type === 'sms' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Provider</label>
                  <select
                    value={configForm.provider || 'twilio'}
                    onChange={(e) => setConfigForm({ ...configForm, provider: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="twilio">Twilio</option>
                    <option value="nexmo">Nexmo</option>
                    <option value="aws_sns">AWS SNS</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Account SID</label>
                  <input
                    type="text"
                    value={configForm.account_sid || ''}
                    onChange={(e) => setConfigForm({ ...configForm, account_sid: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Auth Token</label>
                  <input
                    type="password"
                    value={configForm.auth_token || ''}
                    onChange={(e) => setConfigForm({ ...configForm, auth_token: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">From Number</label>
                  <input
                    type="tel"
                    value={configForm.from_number || ''}
                    onChange={(e) => setConfigForm({ ...configForm, from_number: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="+1234567890"
                  />
                </div>
              </>
            )}

            {integration.type === 'calendar' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Client ID</label>
                  <input
                    type="text"
                    value={configForm.client_id || ''}
                    onChange={(e) => setConfigForm({ ...configForm, client_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Client Secret</label>
                  <input
                    type="password"
                    value={configForm.client_secret || ''}
                    onChange={(e) => setConfigForm({ ...configForm, client_secret: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    To connect Google Calendar, you'll need to create OAuth credentials in the Google Cloud Console
                    and add {window.location.origin}/api/auth/google/callback as a redirect URI.
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowConfigModal(null)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveConfig}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Integration Settings</h2>
        <p className="text-gray-600">Connect external services and manage API access</p>
      </div>

      {/* Available Integrations */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Available Integrations</h3>
        </div>
        <div className="divide-y">
          {integrations.filter(i => i.type !== 'api').map((integration) => (
            <div key={integration.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <div className={`p-2 rounded-lg mr-4 ${
                    integration.status === 'connected'
                      ? 'bg-green-100'
                      : integration.status === 'error'
                      ? 'bg-red-100'
                      : 'bg-gray-100'
                  }`}>
                    {integration.icon}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{integration.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{integration.description}</p>
                    {integration.status === 'connected' && integration.lastSync && (
                      <p className="text-xs text-gray-500 mt-2">
                        Last synced: {new Date(integration.lastSync).toLocaleString()}
                      </p>
                    )}
                    {integration.status === 'error' && integration.errorMessage && (
                      <p className="text-xs text-red-600 mt-2">{integration.errorMessage}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {integration.status === 'connected' && (
                    <>
                      <button
                        onClick={() => handleTest(integration.id)}
                        disabled={testingId === integration.id}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        {testingId === integration.id ? (
                          <span className="flex items-center">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-1"></div>
                            Testing...
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <TestTube className="w-3 h-3 mr-1" />
                            Test
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => handleConfigure(integration)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <Settings className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDisconnect(integration.id)}
                        className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                      >
                        Disconnect
                      </button>
                    </>
                  )}
                  {integration.status === 'disconnected' && (
                    <button
                      onClick={() => handleConfigure(integration)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API Access */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">API Access</h3>
          <button
            onClick={() => setShowApiKeys(!showApiKeys)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {showApiKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <button
              onClick={generateApiKey}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Generate New API Key
            </button>
          </div>

          <div className="space-y-3">
            <div className="text-sm text-gray-600">
              <p>API Endpoint: <code className="bg-gray-100 px-2 py-1 rounded">{window.location.origin}/api</code></p>
              <p className="mt-2">Documentation: <a href="/api/docs" className="text-blue-600 hover:underline">View API Docs</a></p>
            </div>

            {/* Placeholder for API keys list */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">No API keys generated yet</p>
            </div>
          </div>
        </div>
      </div>

      {/* Webhook Events */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Webhook Events</h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            When webhooks are configured, the following events can trigger notifications:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              'chore.assigned',
              'chore.completed',
              'chore.verified',
              'chore.rejected',
              'points.earned',
              'points.redeemed',
              'reward.claimed',
              'user.login',
              'user.created'
            ].map(event => (
              <div key={event} className="flex items-center text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                {event}
              </div>
            ))}
          </div>
        </div>
      </div>

      {renderConfigModal()}
    </div>
  )
}